import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ShieldCheck, Download, RefreshCw, Copy, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * /app/director/forensics — sealed forensic reports.
 *
 * Pulls the enrollment breakdown report from /director/forensics/enrollment
 * and renders it with a Sovereign Seal footer the recipient can verify.
 */
export default function DirectorForensics() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/director/forensics/enrollment");
            setReport(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not pull forensic report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const copy = async (text, what) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(what);
            setTimeout(() => setCopied(""), 1500);
        } catch { toast.error("Clipboard unavailable."); }
    };

    const downloadJson = () => {
        if (!report) return;
        const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `enrollment-breakdown-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Sealed report downloaded.");
    };

    const seal = report?.sovereign_seal;

    return (
        <div className="space-y-8" data-testid="director-forensics-page">
            <div className="fade-up flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Sovereign Forensics</div>
                    <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Enrollment Breakdown</h1>
                    <p className="text-muted-foreground mt-2 max-w-2xl">
                        Tamper-proof enrollment report. Every deliverable carries an HMAC-SHA256 seal.
                        Recipients verify via <code className="text-[hsl(45_85%_70%)]">/api/sovereign/verify</code> — no EIGOO secrets required.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="forensics-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_13%)] transition-colors"
                    >
                        <RefreshCw size={13} /> Reload
                    </button>
                    <button
                        onClick={downloadJson}
                        disabled={!report}
                        data-testid="forensics-download"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-40"
                    >
                        <Download size={13} /> Download Sealed JSON
                    </button>
                </div>
            </div>

            {loading || !report ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-xl bg-[hsl(151_55%_13%)]" />)}
                </div>
            ) : (
                <>
                    {/* Totals */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 fade-up">
                        <Stat label="All Users" value={report.totals.all_users} accent="hsl(40 50% 60%)" />
                        <Stat label="Director Seeds" value={report.totals.director_seeds} accent="hsl(45 85% 65%)" subtle="excluded from organic" />
                        <Stat label="Organic Enrollments" value={report.totals.organic_enrollments} accent="hsl(151 60% 60%)" subtle="external boards" />
                    </div>

                    {/* By role */}
                    <div className="glass rounded-2xl p-6 fade-up">
                        <div className="font-heading text-2xl mb-4">By Role · Organic</div>
                        {report.by_role.length === 0 ? (
                            <div className="text-sm text-muted-foreground py-4">No organic enrollments yet.</div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {report.by_role.map((r) => (
                                    <div key={r.role} className="rounded-lg border border-[hsl(40_40%_30%/0.3)] bg-[hsl(151_55%_10%/0.6)] p-4 flex items-center justify-between">
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground truncate">{r.role.replace(/_/g, " ")}</div>
                                        <div className="font-heading text-2xl">{r.count}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Recent roster */}
                    <div className="glass rounded-2xl p-6 fade-up">
                        <div className="flex items-baseline justify-between mb-4 gap-2 flex-wrap">
                            <div className="font-heading text-2xl">Recent Roster</div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{report.recent_roster.length} most-recent</div>
                        </div>
                        {report.recent_roster.length === 0 ? (
                            <div className="text-sm text-muted-foreground py-4">No external operators yet.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                        <tr><th className="text-left py-2">Operator</th><th className="text-left">Role</th><th className="text-left">State</th><th className="text-left">Joined</th><th className="text-left">Source</th></tr>
                                    </thead>
                                    <tbody>
                                        {report.recent_roster.map((u) => (
                                            <tr key={u.email} className="border-t border-[hsl(40_30%_25%/0.2)]">
                                                <td className="py-2 pr-2 truncate"><div className="text-foreground">{u.name || u.email}</div><div className="text-[10px] text-muted-foreground">{u.email}</div></td>
                                                <td className="text-xs">{u.role || "—"}{u.role_request && u.role_request !== u.role && <span className="ml-1 text-[10px] text-[hsl(45_85%_70%)]">→ {u.role_request} ({u.role_request_status || "pending"})</span>}</td>
                                                <td className="text-xs">{(u.territories?.[0]) || "—"}</td>
                                                <td className="text-xs text-muted-foreground">{(u.joined_at || "").slice(0, 10)}</td>
                                                <td className="text-xs">{u.via_referral ? <span className="text-[hsl(45_85%_70%)]">via referral</span> : <span className="text-muted-foreground">direct</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Sovereign Seal footer */}
                    {seal && (
                        <div className="rounded-2xl border-2 border-[hsl(45_75%_55%/0.4)] bg-[hsl(40_50%_8%/0.5)] p-6 fade-up" data-testid="sovereign-seal-footer">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[hsl(45_85%_60%/0.15)] text-[hsl(45_85%_70%)]">
                                    <ShieldCheck size={18} />
                                </div>
                                <div>
                                    <div className="font-heading text-xl text-[hsl(45_85%_75%)]">EIGOO Sovereign Seal</div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{seal.deliverable} · {seal.issuer}</div>
                                </div>
                            </div>
                            <SealRow label="sha256"    value={seal.sha256}    onCopy={() => copy(seal.sha256, "sha")}    copied={copied === "sha"} />
                            <SealRow label="signature" value={seal.signature} onCopy={() => copy(seal.signature, "sig")} copied={copied === "sig"} />
                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[10px] uppercase tracking-wider">
                                <Meta label="key_id"     value={seal.key_id} />
                                <Meta label="version"    value={seal.version} />
                                <Meta label="signed_at"  value={(seal.signed_at || "").slice(0, 19) + "Z"} />
                                <Meta label="verify"     value="/api/sovereign/verify" />
                            </div>
                            <div className="mt-4 text-xs text-muted-foreground">
                                <FileText size={11} className="inline -mt-px mr-1" /> Recipients verify by POSTing the sealed JSON
                                back to <code className="font-mono px-1.5 py-0.5 rounded bg-[hsl(45_50%_15%)]">{seal.verify_endpoint}</code> ·
                                response will show <code className="text-[hsl(151_60%_70%)]">verified: true</code> if untampered.
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Stat({ label, value, accent, subtle }) {
    return (
        <div className="glass rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className="font-heading text-3xl mt-1" style={{ color: accent }}>{value}</div>
            {subtle && <div className="text-[10px] text-muted-foreground mt-1">{subtle}</div>}
        </div>
    );
}

function SealRow({ label, value, onCopy, copied }) {
    return (
        <div className="flex items-center gap-3 mt-2 group">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground w-20 shrink-0">{label}</div>
            <code className="font-mono text-[11px] text-[hsl(45_85%_72%)] break-all flex-1">{value}</code>
            <button
                onClick={onCopy}
                className="shrink-0 p-1.5 rounded-md border border-[hsl(40_40%_30%/0.3)] text-[hsl(45_85%_70%)] hover:bg-[hsl(45_50%_15%)] transition-colors opacity-60 group-hover:opacity-100"
                aria-label={`Copy ${label}`}
                data-testid={`seal-copy-${label}`}
            >
                {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
            </button>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <div className="text-muted-foreground">{label}</div>
            <div className="text-foreground/85 font-mono text-[11px] mt-0.5 break-all">{value}</div>
        </div>
    );
}
